import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, Spinner } from "react-bootstrap";
import { FaPaperclip } from "react-icons/fa";
import styles from "./ThreadedMessageViewer.module.css";
import { resolveName } from "./utils/resolveName"; // Adjust path as needed
import { isSelfMessage, getMessageAttachments } from "./utils/messageUtils";
import DoctorFemaleIcon from "./assets/Doctor_Female_96.png";
import DoctorMaleIcon from "./assets/Doctor_Male_96.png";
import GenericFHIRViewerModal from "./cards/GenericFHIRViewerModal";
import QuickResponse from "./QuickResponse";
import { createWrappedMessage, getBinaryResourcesFromDocumentReference, buildAgentFromContextProvider, buildEntityFromContextPatient } from "./utils/messageUtils";
import { buildErrorAuditEvent } from "./services/auditEventBuilder";
import { logEvent } from "./services/logService";
import AttachmentErrorModal from "./modals/AttachmentErrorModal"; // adjust path

function ThreadMessageViewer({ selectedComm, fhirClient, contextProvider, userSelection, fetchedPatient, fetchedProvider, fetchedEncounter, communicationService, setShowThanksModal, setShowReplyModal, setSelectedComm, selectMessageById }) {
    const byId = useSelector(state => state.messages.byId);
    const threadMap = useSelector(state => state.messages.threadMap);
    const providers = useSelector(state => state.providers);
    const [threadTitle, setThreadTitle] = useState("Conversation");
    const [nameMap, setNameMap] = useState({});
    const [loadingNames, setLoadingNames] = useState(true);
    const rawAnchor = selectedComm?.partOf?.[0]?.reference;
    const fallbackAnchor = `Communication/${selectedComm?.id}`;
    // ✅ Use rawAnchor only if it's defined and not "unknown"
    const validAnchorRef = rawAnchor && rawAnchor !== "unknown" ? rawAnchor : fallbackAnchor;
    const anchorId = validAnchorRef?.split("/")[1];
    const threadIds = threadMap?.[anchorId] || [];
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [showProviderModal, setShowProviderModal] = useState(false);
    const [providerModalDetails, setProviderModalDetails] = useState(null);
    const handleProviderModalClose = () => setShowProviderModal(false);
    const { fetchAttachmentBlob } = require("./utils/fetchAttachmentBlob");
    const messages = useMemo(() => {
        return threadIds
            .map(id => byId[id])
            .filter(Boolean)
            .sort((a, b) => new Date(b.sent) - new Date(a.sent));
    }, [threadIds, byId]);

    useEffect(() => {
    }, [byId]);

    const referencesToResolve = useMemo(() => {
        const refs = new Set();
        messages.forEach(msg => {
            if (msg.sender?.reference) refs.add(msg.sender.reference);
            (msg.recipient || []).forEach(r => r?.reference && refs.add(r.reference));
            (msg.extension || []).forEach(ext =>
                (ext.extension || []).forEach(inner => {
                    const ref = inner?.valueReference?.reference;
                    if (ref) refs.add(ref);
                })
            );
        });

        return Array.from(refs);
    }, [messages]);

    const providerName = providerModalDetails?.name?.[0];
    const providerFullName = providerName
        ? `${providerName.prefix?.join(" ") || ""} ${providerName.given?.join(" ")} ${providerName.family || ""}`.trim()
        : "Provider";



    //console.log(fetchedEncounter);

    useEffect(() => {
        // Define an async function to resolve display names for references
        async function resolveAllNames() {
            setLoadingNames(true); // Indicate that name resolution is in progress

            // Filter out references that have already been resolved
            const unresolvedRefs = referencesToResolve.filter(ref => !nameMap[ref]);

            // If all references are already resolved, exit early
            if (unresolvedRefs.length === 0) {
                setLoadingNames(false);
                return;
            }

            // Resolve each unresolved reference in parallel
            const entries = await Promise.all(
                unresolvedRefs.map(async ref => {
                    const name = await resolveName(ref, { getState: () => ({ providers }) }, fhirClient);
                    return [ref, name]; // Return a tuple of [reference, resolved name]
                })
            );

            // Update the name map with newly resolved entries
            setNameMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));

            setLoadingNames(false); // Mark resolution as complete
        }

        // Only attempt to resolve names if there are messages present
        if (messages.length > 0) {
            resolveAllNames();
        } else {
            setLoadingNames(false); // No messages, so nothing to resolve
        }
    }, [referencesToResolve, providers, fhirClient]);



    useEffect(() => {
        if (!loadingNames) {
            const participantsSet = new Set();

            for (const msg of messages) {
                const senderRef = msg.sender?.reference;
                if (senderRef) participantsSet.add(nameMap[senderRef] || senderRef);

                const recipients = msg.recipient;
                if (Array.isArray(recipients)) {
                    for (const r of recipients) {
                        const recipientRef = r?.reference;
                        if (recipientRef) participantsSet.add(nameMap[recipientRef] || recipientRef);
                    }
                }
            }

            const participants = [...participantsSet].join(", ");
            setThreadTitle(participants || "Conversation");
        }
    }, [messages, nameMap, loadingNames]);

    return (
        <div>
            <Card className="p-2 border border-dark bg-body-secondary">
                <Card.Header className={`d-flex justify-content-between align-items-center mb-0 ${styles.conversationHeader}`}>
                    <h5 className="mb-0">
                        Participants:&nbsp;
                        {threadTitle.split(", ").map((nameOrRef, idx, arr) => {
                            // Try to find provider by reference or by resolved name
                            let provider = null;
                            let providerId = null;

                            if (nameOrRef.startsWith("Practitioner/")) {
                                providerId = nameOrRef.split("/")[1];
                                provider = providers[providerId];
                            } else {
                                // Try to match by resolved name
                                provider = Object.values(providers).find(p => {
                                    const fullName =
                                        (p.name?.[0]?.text ||
                                            `${p.name?.[0]?.given?.join(" ")} ${p.name?.[0]?.family}`)?.trim();
                                    return fullName === nameOrRef;
                                });
                                providerId = provider?.id;
                            }

                            // If still not found, check if it's the in-context provider
                            if (!provider && contextProvider) {
                                const contextFullName =
                                    (contextProvider.name?.[0]?.text ||
                                        `${contextProvider.name?.[0]?.given?.join(" ")} ${contextProvider.name?.[0]?.family}`)?.trim();
                                if (
                                    nameOrRef === contextFullName ||
                                    nameOrRef === `Practitioner/${contextProvider.id}`
                                ) {
                                    provider = contextProvider;
                                    providerId = contextProvider.id;
                                }
                            }

                            const gender = provider?.gender;
                            const iconSrc = gender === "female" ? DoctorFemaleIcon : DoctorMaleIcon;

                            return (
                                <span key={idx} className="d-inline-flex align-items-center me-2">
                                    {provider ? (
                                        <span
                                            onClick={() => {
                                                setProviderModalDetails(provider);
                                                setShowProviderModal(true);
                                            }}
                                            className={styles.iconWrapper}
                                            title={`View ${nameOrRef} details`}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <img
                                                src={iconSrc}
                                                alt="Doctor Icon"
                                                className={styles.genderIcon}
                                                style={{ marginRight: "4px" }}
                                            />
                                        </span>
                                    ) : null}
                                    <span>{nameOrRef}</span>
                                    {idx < arr.length - 1 && <span>,&nbsp;</span>}
                                </span>
                            );
                        })}
                    </h5>
                </Card.Header>
                {fetchedEncounter && typeof fetchedEncounter === "object" && "error" in fetchedEncounter && (
                    <>
                        {fetchedEncounter.error === 401 || fetchedEncounter.error === 410 ? (
                            <div className="p-3 text-center text-danger fw-bold border-top border-dark bg-body">
                                Encounter removed. Please start a new conversation.
                            </div>
                        ) : (
                            <div className="p-3 text-center text-danger fw-bold border-top border-dark bg-body">
                                Error loading encounter (code: {fetchedEncounter.error}).
                            </div>
                        )}
                    </>
                )}

                {(!fetchedEncounter?.error) && (
                    <QuickResponse
                        selectedComm={selectedComm}
                        fetchedProvider={fetchedProvider}
                        fhirClient={fhirClient}
                        contextProvider={contextProvider}
                        communicationService={communicationService}
                        fetchedPatient={fetchedPatient}
                        fetchedEncounter={fetchedEncounter}
                        setShowThanksModal={setShowThanksModal}
                        setShowReplyModal={setShowReplyModal}
                        onSendLocalReply={(msg) => {
                            setSelectedComm((prev) => ({
                                ...prev,
                                injectedMessages: [...(prev?.injectedMessages || []), msg],
                            }));
                        }}
                    />
                )}


                {loadingNames && (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                        <Spinner animation="border" role="status" />
                        <span className="ms-2">Resolving names...</span>
                    </div>
                )}

                {!loadingNames && messages.length === 0 && <p>Select a message to load conversation.</p>}

                {!loadingNames && messages.map(msg => {
                    const senderRef = msg.sender?.reference || "Unknown Sender";
                    const sender = nameMap[senderRef] || senderRef;
                    const content = msg.payload?.[0]?.contentString || "[No content]";

                    const readReceipts = (msg.extension || [])
                        .map(ext => {
                            const userEntry = ext.extension?.find(e => e.url === "user");
                            const readAtEntry = ext.extension?.find(e => e.url === "readAt");

                            const reference = userEntry?.valueReference?.reference;
                            const readAt = readAtEntry?.valueDateTime;

                            return reference ? { reference, readAt } : null;
                        })
                        .filter(Boolean)
                        .filter(receipt => receipt.reference !== senderRef);


                    //console.log("Read receipts for message", msg.id, readReceipts);

                    const attachments = getMessageAttachments(msg);

                    return (
                        <Card
                            key={msg.id}
                            className={`mb-3 shadow-sm ${isSelfMessage(msg, contextProvider.id) ? styles.selfMessage : styles.receivedMessage} ${msg.id === selectedComm?.id ? styles.selectedCard : ""}`}>
                            <Card.Body>
                                <Card.Title className={styles.customTitle}>
                                    {(() => {
                                        const senderId = senderRef?.includes("/") ? senderRef.split("/")[1] : senderRef;
                                        const provider = providers[senderId];
                                        const isSelfMessage = senderId === contextProvider?.id;

                                        if (!provider || isSelfMessage) return null; // 👈 Skip icon for self messages

                                        const handleIconClick = () => {
                                            setProviderModalDetails(provider);
                                            setShowProviderModal(true);
                                        };

                                        const gender = provider?.gender;
                                        const iconSrc = gender === "female" ? DoctorFemaleIcon : DoctorMaleIcon;

                                        return (
                                            <span onClick={handleIconClick} className={styles.iconWrapper} title="View Provider Details">
                                                <img
                                                    src={iconSrc}
                                                    alt="Doctor Icon"
                                                    className={styles.genderIcon}
                                                />
                                            </span>
                                        );
                                    })()}
                                    {sender}
                                    {msg.sent && (
                                        <span className="text-muted ms-2">
                                            {(() => {
                                                const sentDate = new Date(msg.sent);
                                                const now = new Date();
                                                const sentDay = sentDate.getDate();
                                                const sentMonth = sentDate.getMonth();
                                                const sentYear = sentDate.getFullYear();

                                                const nowDay = now.getDate();
                                                const nowMonth = now.getMonth();
                                                const nowYear = now.getFullYear();

                                                // Check if today
                                                if (sentDay === nowDay && sentMonth === nowMonth && sentYear === nowYear) {
                                                    return (
                                                        <>
                                                            (today,{" "}
                                                            {sentDate.toLocaleTimeString(undefined, {
                                                                hour: "2-digit",
                                                                minute: "2-digit"
                                                            })})
                                                        </>
                                                    );
                                                }

                                                // Check if yesterday
                                                const yesterday = new Date(now);
                                                yesterday.setDate(nowDay - 1);
                                                if (
                                                    sentDay === yesterday.getDate() &&
                                                    sentMonth === yesterday.getMonth() &&
                                                    sentYear === yesterday.getFullYear()
                                                ) {
                                                    return (
                                                        <>
                                                            (yesterday,{" "}
                                                            {sentDate.toLocaleTimeString(undefined, {
                                                                hour: "2-digit",
                                                                minute: "2-digit"
                                                            })})
                                                        </>
                                                    );
                                                }

                                                // Default: show date as before, in brackets
                                                return `[${sentDate.toLocaleString(undefined, {
                                                    dateStyle: "medium",
                                                    timeStyle: "short"
                                                })}]`;
                                            })()}
                                        </span>
                                    )}
                                </Card.Title>
                                <Card.Text>{content}</Card.Text>
                                {attachments.length > 0 && (
                                    <div className="mt-2">
                                        <div className="fw-bold small">Attachments:</div>
                                        <ul className="list-unstyled mb-0">
                                            {attachments.map((att, i) => (
                                                <li key={i}>
                                                    <button
                                                        className="btn btn-link p-0"
                                                        onClick={async () => {
                                                            const result = await fetchAttachmentBlob(att, fhirClient);
                                                            console.log(result);

                                                            if (result.deleted) {
                                                                console.log("Attachment deleted");
                                                                setModalMessage(
                                                                    `⚠️ This attachment is no longer available.\n\n${result.reason || "It was deleted."}`
                                                                );
                                                                setShowModal(true);
                                                                return;
                                                            }

                                                            if (!result.blob) {
                                                                setModalMessage(
                                                                    `❌ Could not fetch attachment.\n\n${result.reason || "Unknown error."}`
                                                                );
                                                                setShowModal(true);
                                                                return;
                                                            }

                                                            const blobUrl = URL.createObjectURL(result.blob);
                                                            const a = document.createElement("a");
                                                            a.href = blobUrl;
                                                            a.download = att.title || `Attachment-${i + 1}`;
                                                            a.click();
                                                        }}
                                                    >
                                                        <FaPaperclip className="me-1" />
                                                        {att.title || `Attachment ${i + 1}`} ({att.contentType})
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {readReceipts.length > 0 && (() => {
                                    // Filter entries with a valid readAt date
                                    const validEntries = readReceipts.filter(entry => entry.readAt);

                                    // Deduplicate by resolved name
                                    const seenNames = new Set();
                                    const uniqueEntries = validEntries.filter(entry => {
                                        const name = nameMap[entry.reference] || entry.reference;
                                        if (!name || seenNames.has(name)) return false;
                                        seenNames.add(name);
                                        return true;
                                    });

                                    return uniqueEntries.length > 0 && (
                                        <div>
                                            <p></p>
                                            Read by:
                                            <ul
                                                style={{
                                                    paddingLeft: "1rem",
                                                    marginTop: "0.5rem",
                                                    listStyleType: "none",
                                                }}
                                            >
                                                {uniqueEntries.map((entry, index) => {
                                                    const name = nameMap[entry.reference] || entry.reference;
                                                    return (
                                                        <li key={index}>
                                                            👁️ {name}
                                                            <span className="ms-2 text-muted">
                                                                on{" "}
                                                                {new Date(entry.readAt).toLocaleString(undefined, {
                                                                    dateStyle: "medium",
                                                                    timeStyle: "short",
                                                                })}
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    );
                                })()}
                            </Card.Body>
                        </Card>
                    );
                })}
            </Card>

            <GenericFHIRViewerModal
                show={showProviderModal}
                onClose={handleProviderModalClose}
                resource={providerModalDetails}
                title={providerFullName}
            />

            <AttachmentErrorModal
                show={showModal}
                onClose={() => setShowModal(false)}
                message={modalMessage}
            />
        </div>


    );
}

export default ThreadMessageViewer;